import React, { useMemo, useState } from 'react';
import useSWR from 'swr';
import DataTable, { Column } from '../../../components/DataTable/DataTable';
import { fetcher } from '../../../services/fetcher';
import { useTheme } from '../../../contexts/ThemeContext';
import { Badge, Button, Card, SearchIcon, Switch } from '../../../components/ui';
import { formatDate as formatDateGlobal } from '../../../config/appConfig';
import { cn } from '../../../lib/utils';
import { ACCOUNTING_TABLE_DEFAULTS } from '../shared/typography';
import { AccountingReportShell } from './shared/AccountingReportShell';
import { PeriodDateRangeFields, formatReportAmount } from './shared/reportUtils';
import { buildReportQuery, useAccountingReportFilters } from './shared/useAccountingReportFilters';

type AmountBlock = {
  totalBalance: number;
  periodBalance: number;
};

type Row = {
  accountId: number;
  levelCode: string;
  levelName: string;
  byCompany: Record<number, AmountBlock>;
  totals: AmountBlock;
};

type Company = { id: number; code: string; name: string };

type Response = {
  companies: Company[];
  rows: Row[];
  totals: { byCompany: Record<number, AmountBlock>; grand: AmountBlock };
  recordCount: number;
};

export const TrialBalanceByCompany: React.FC = () => {
  const { colors } = useTheme();
  const f = useAccountingReportFilters();
  const [useLocalCurrency, setUseLocalCurrency] = useState(true);
  const [includeZeroValues, setIncludeZeroValues] = useState(false);

  const reportUrl =
    f.run
      ? buildReportQuery('/accounting/reports/trial-balance-by-company', {
          dateFrom: f.dateFrom,
          dateTo: f.dateTo,
          companyIds: f.companyIdsParam || undefined,
          useLocalCurrency,
          includeZeroValues,
        })
      : null;

  const { data, isLoading } = useSWR<Response>(reportUrl, fetcher);

  const columns = useMemo<Column<Row>[]>(() => {
    if (!data) {
      return [
        { key: 'levelCode', header: 'Code' },
        { key: 'levelName', header: 'Name' },
      ];
    }
    const cols: Column<Row>[] = [
      { key: 'levelCode', header: 'Code', width: '9rem', sortable: true },
      { key: 'levelName', header: 'Name', width: '14rem', sortable: true },
    ];
    for (const company of data.companies) {
      cols.push({
        key: `company_${company.id}` as keyof Row,
        header: 'Balance',
        align: 'right',
        columnGroup: { id: String(company.id), header: company.code },
        render: (_v, row) => formatReportAmount(row.byCompany[company.id]?.totalBalance ?? 0),
        footerType: 'custom',
        footerRender: () =>
          formatReportAmount(data.totals.byCompany[company.id]?.totalBalance ?? 0),
      });
    }
    cols.push({
      key: 'totals',
      header: 'Total',
      align: 'right',
      columnGroup: { id: 'grand', header: 'Grand' },
      render: (_v, row) => formatReportAmount(row.totals.totalBalance),
      footerType: 'custom',
      footerRender: () => formatReportAmount(data.totals.grand?.totalBalance ?? 0),
    });
    return cols;
  }, [data]);

  const toolbar = (
    <Card className="p-4 shrink-0 space-y-3">
      <div className="flex flex-wrap items-end gap-x-4 gap-y-3">
        <PeriodDateRangeFields
          periodId={f.periodId}
          periodOptions={f.periodOptions}
          onPeriodChange={(value) => {
            f.setPeriodId(value);
            f.setRun(false);
          }}
          dateFrom={f.dateFrom}
          dateTo={f.dateTo}
          onDateFromChange={(value) => {
            f.setPeriodId('');
            f.setDateFrom(value);
            f.setRun(false);
          }}
          onDateToChange={(value) => {
            f.setPeriodId('');
            f.setDateTo(value);
            f.setRun(false);
          }}
        />
      </div>
      <div className={cn('flex flex-wrap items-center gap-x-6 gap-y-2 pt-3 border-t', colors.border)}>
        <div className="flex items-center gap-6 shrink-0">
          <Switch label="Local currency" checked={useLocalCurrency} onChange={(e) => { setUseLocalCurrency(e.target.checked); f.setRun(false); }} size="sm" />
          <Switch label="Include zero" checked={includeZeroValues} onChange={(e) => { setIncludeZeroValues(e.target.checked); f.setRun(false); }} size="sm" />
        </div>
        <div className="flex items-center gap-2 ml-auto shrink-0">
          <Badge variant="secondary" className="h-10 px-3 flex items-center gap-1.5">
            <span className={cn('h-1.5 w-1.5 rounded-full', f.allCompanies ? 'bg-emerald-500' : 'bg-amber-500')} />
            {f.companyScopeLabel}
          </Badge>
          <Button variant="default" onClick={() => f.setRun(true)} disabled={!f.canSearch} className="h-10 px-5 gap-2">
            <SearchIcon className="h-4 w-4" />
            Search
          </Button>
        </div>
      </div>
    </Card>
  );

  const rows = data?.rows ?? [];

  return (
    <AccountingReportShell
      toolbar={toolbar}
      run={f.run}
      loading={isLoading}
      rows={rows}
      columns={columns}
      entityName="Trial Balance by Company"
      exportFileBase="trial-balance-by-company"
      exportDateTo={f.dateTo}
      statusBadge={
        f.run ? (
          <Badge variant="info" className="font-normal">
            {formatDateGlobal(f.dateFrom)} – {formatDateGlobal(f.dateTo)}
          </Badge>
        ) : undefined
      }
      statusExtra={
        <Badge variant="secondary" className="font-normal">
          {useLocalCurrency ? 'Local currency' : 'USD'}
        </Badge>
      }
      recordCount={data?.recordCount ?? rows.length}
      readyMessage="Choose period and companies, then search."
    >
      <DataTable
        {...ACCOUNTING_TABLE_DEFAULTS}
        data={rows}
        columns={columns}
        keyExtractor={(r) => String(r.accountId)}
        showFooter
        stickyHeader
        emptyMessage="No data."
      />
    </AccountingReportShell>
  );
};
